import React, { useEffect, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { MessageCircleIcon, XIcon } from "lucide-react";
import { EASE } from "../utils/motion";
import { MapleLeaf } from "./MapleLeaf";
type ChatLauncherProps = {
  pathname: string;
  hidden: boolean;
  onOpen: (question?: string) => void;
};

// Pages where families tend to have the most questions, with a contextual prompt.
const PROMPTS: Record<string, string> = {
  "/": "New to the OSSD? Ask me how it works.",
  "/about": "Want to know what makes MCC different?",
  "/academics": "Questions about what your child will study?",
  "/academics/curriculum": "Wondering which courses suit your child?",
  "/academics/pathways": "Curious where the OSSD can lead?",
  "/academics/scholarships": "Want to know about the MCC Scholarship?",
  "/admissions": "Not sure where to start? I can help.",
  "/admissions/requirements": "Unsure which documents you need?",
  "/admissions/fast-track": "Wondering if Fast Track suits your child?",
  "/academics/summer-school": "Want the Summer School dates first?",
  "/admissions/fees": "Questions about fees or payment plans?",
  "/admissions/apply": "Need help with your application?",
  "/boarding": "Questions about boarding and safety?",
  "/contact": "Want to arrange a campus visit?"
};
const FIRST_NUDGE_MS = 7000;
const NUDGE_EVERY_MS = 35000;
const NUDGE_VISIBLE_MS = 7000;
const DISMISS_KEY = "mcc-nudges-dismissed";
export function ChatLauncher({
  pathname,
  hidden,
  onOpen
}: ChatLauncherProps) {
  const reduce = useReducedMotion();
  const prompt = PROMPTS[pathname];
  const [nudge, setNudge] = useState(false);
  const [dismissed, setDismissed] = useState(() => typeof window !== "undefined" && window.sessionStorage.getItem(DISMISS_KEY) === "1");
  useEffect(() => {
    setNudge(false);
    if (!prompt || dismissed || hidden) return;
    let hideTimer: number | undefined;
    const show = () => {
      setNudge(true);
      hideTimer = window.setTimeout(() => setNudge(false), NUDGE_VISIBLE_MS);
    };
    const first = window.setTimeout(show, FIRST_NUDGE_MS);
    const repeat = window.setInterval(show, NUDGE_EVERY_MS);
    return () => {
      window.clearTimeout(first);
      window.clearInterval(repeat);
      window.clearTimeout(hideTimer);
    };
  }, [pathname, prompt, dismissed, hidden]);
  if (!prompt) return null;
  function dismiss() {
    setNudge(false);
    setDismissed(true);
    window.sessionStorage.setItem(DISMISS_KEY, "1");
  }
  function open(withPrompt: boolean) {
    setNudge(false);
    window.dispatchEvent(new CustomEvent("mcc:conversion", {
      detail: {
        eventName: "ask_maple_launcher_open",
        pathname
      }
    }));
    onOpen(withPrompt ? prompt : undefined);
  }
  return <AnimatePresence data-id="element-143" data-mp="3cxz6s82gipr8">
      {!hidden && <motion.div initial={{
      opacity: 0,
      scale: reduce ? 1 : 0.9,
      y: reduce ? 0 : 12
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={{
      opacity: 0,
      scale: reduce ? 1 : 0.9
    }} transition={{
      duration: reduce ? 0.01 : 0.3,
      delay: reduce ? 0 : 1.6,
      ease: EASE
    }} className="fixed bottom-5 right-5 z-[60] flex items-end gap-3 md:bottom-7 md:right-7" data-id="element-144" data-mp="3cwxkt8jwkiuq">
          <AnimatePresence data-id="element-145" data-mp="2ovlqu8bbek40">
            {nudge && <motion.div role="status" initial={{
          opacity: 0,
          x: reduce ? 0 : 12,
          scale: reduce ? 1 : 0.96
        }} animate={{
          opacity: 1,
          x: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          x: reduce ? 0 : 8
        }} transition={{
          duration: reduce ? 0.01 : 0.25,
          ease: EASE
        }} className="relative mb-2 max-w-[250px] border border-[#dedbd3] bg-[#fbfaf6] py-3 pl-4 pr-9 text-[#061f38] shadow-[0_16px_40px_rgba(5,26,48,0.18)]" data-id="element-146" data-mp="2tt44ud46vpiv">
                <button type="button" onClick={() => open(true)} className="text-left" data-id="element-147" data-mp="1w53sfkljd6wx">
                  <span className="inline-flex items-center gap-1.5 text-[10px] font-bold tracking-[0.18em] text-[#bb1e35]" data-id="element-148" data-mp="214gh8avywo25"><MapleLeaf size={10} data-id="element-149" data-mp="2hcctumgplu26" /> ASK MAPLE</span>
                  <span className="mt-1 block text-sm leading-5" data-id="element-150" data-mp="2hdxnm9v798yz">{prompt}</span>
                </button>
                <button type="button" onClick={dismiss} aria-label="Dismiss suggestions" className="absolute right-2 top-2 grid h-6 w-6 place-items-center text-[#6b7378] hover:text-[#061f38]" data-id="element-151" data-mp="2cj1koeofd173">
                  <XIcon size={13} data-id="element-152" data-mp="22ku5o1dp4t7q" />
                </button>
              </motion.div>}
          </AnimatePresence>

          <button type="button" onClick={() => open(false)} aria-label="Open Ask Maple assistant" className="relative grid h-14 w-14 shrink-0 place-items-center rounded-full bg-[#c5233d] text-white shadow-[0_12px_30px_rgba(6,31,56,0.35)] transition-[transform,background-color] duration-200 ease-out hover:-translate-y-0.5 hover:bg-[#a71931] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#c5233d] focus-visible:ring-offset-2 active:scale-95" data-id="element-153" data-mp="2vbvoccgll6w0">
            {nudge && !reduce && <motion.span aria-hidden="true" initial={{
          opacity: 0.55,
          scale: 1
        }} animate={{
          opacity: 0,
          scale: 1.7
        }} transition={{
          duration: 1.4,
          repeat: 2,
          ease: "easeOut"
        }} className="absolute inset-0 rounded-full bg-[#c5233d]" data-id="element-154" data-mp="3ln7k7xoffg38" />}
            <MessageCircleIcon size={24} className="relative" data-id="element-155" data-mp="94hqk7nbr0pm" />
          </button>
        </motion.div>}
    </AnimatePresence>;
}